import { cloneDeep as _cloneDeep } from 'lodash';
import { type PlacementDetailsWorkRestriction } from 'src/store/placementDetailsPageSlice/types';
import { validateWeightRestrictionTaskGroupChildren } from 'src/utils/restrictionUtils';
import { validateRestrictedValue } from 'src/utils/restrictionUtilsLegacy';

import { type ValidationTaskGroup } from './types';

// This validation utility is tightly coupled to the hook within this folder. We locate them together to maintain
// sufficient co-location of related concerns.
export const restrictionsHaveError = (
  workRestrictions: PlacementDetailsWorkRestriction[] = [],
  modifiedShiftLength: number = 0,
  taskGroups: ValidationTaskGroup[],
): boolean => {
  // By returning a reasonable defaults for empty data, we reduce the dependency management burden on the callers of this
  // method.
  if (!workRestrictions.length || !taskGroups.length) {
    // Loading state is expected to be handled by the components dependent on this hook. Fail gracefully and allow
    // menus to work as normal if there is an issue fulfilling the dependencies of this validation
    return false;
  }

  // Validate WorkRestriction entities are not malformed
  const workRestrictionsHaveError = workRestrictions.some((wr) => !validateRestrictedValue(wr, modifiedShiftLength));

  // Validate task groups have no logical inconsistencies
  // There are (micro) optimizations that can be made in the ensuing logic but we opt for the more streamlined appraoch
  // as the number of task groups is not expected to be large enough to have any visible impact on page performance.
  const taskGroupsCloned = _cloneDeep(taskGroups); // ensure update by reference is only local to this function
  const taskGroupChildMap = new Map(taskGroupsCloned.flatMap((tg) => tg.tasks.map((t) => [t.taskId, t])));
  workRestrictions.forEach((wr) => {
    // Update by reference
    if (taskGroupChildMap.has(wr.taskId)) {
      taskGroupChildMap.get(wr.taskId)!.restrictedValue = wr.restrictedValue;
      taskGroupChildMap.get(wr.taskId)!.bodyLocation = wr.bodyLocation;
    }
  });

  const taskGroupsHaveError = taskGroupsCloned.some((tg) =>
    validateWeightRestrictionTaskGroupChildren(tg.tasks).some((t) => !!t.error),
  );

  return workRestrictionsHaveError || taskGroupsHaveError;
};
